import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import Link from 'react-router-dom/Link';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

function EmployeeIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
        // Token is not expired, fetch data
        fetchtableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const fetchtableData = async () => {
        fetchData('EmployeeApi', `/${companyId}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    console.log(response);
                    setData(response.response_data);

                }
            })
            .catch((error) => {
                console.error('Error fetching Employee Details:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    };

    const handleDelete = async () => {
        deleteData('EmployeeApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('EmployeeApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    return (
        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '20px' }}>
            <div style={{ marginTop: '-20px' }}>
                {/* Header Section */}
                <div className="flex flex-wrap justify-between items-center">
                    <h6 className="text-[#134B70] text-lg uppercase tracking-wide font-serif" style={{ fontSize: '14px' }}>
                        Employee
                    </h6>
                    {/* Search */}
                    <input
                        type="text"
                        placeholder="🔍 Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: isFocused ? "240px" : "180px",
                            height: "30px",
                            border: "1px solid #ddd",
                            borderRadius: "20px",
                            padding: "0 14px",
                            fontSize: "12px",
                            transition: "all 0.3s ease",
                            outline: "none",
                        }}
                    />
                    <Tooltip title="Create Employee" arrow>
                        <Button
                            className="linkcolor"
                            component={Link}
                            to="/EmployeeCreate"
                            variant="contained"
                            sx={{
                                backgroundColor: "#134B70",
                                textTransform: "none",
                                borderRadius: "20px",
                                fontSize: "12px",
                                padding: "4px 18px",
                                "&:hover": { backgroundColor: "#0d3b66" },
                            }}
                        >
                            + Create
                        </Button>
                    </Tooltip>
                </div>

                {/* Responsive Table Section */}
                <div className="rounded-lg border border-gray-300" style={{ marginTop: '10px' }}>
                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: "none",
                            maxHeight: 590, // <== height should be set here
                            overflow: "auto",
                            borderRadius: "8px",
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {["Sl.No", "Employee Code", "Name", "Department", "Designation", ""].map((col, index) => (
                                        <TableCell
                                            key={index}
                                            sx={{
                                                background: "linear-gradient(to right, #0D1B2A, #1B2A41)",
                                                color: "white",
                                                fontWeight: 700,
                                                fontSize: 11,
                                                textTransform: "uppercase",
                                                padding: "2px 4px",
                                                borderBottom: "2px solid #0D1B2A",
                                                textAlign: col === "" ? "right" : "left",
                                            }}
                                        >
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredData.map((item, index) => (
                                    <TableRow
                                        key={item.Id}
                                        sx={{
                                            "&:hover": { backgroundColor: "#f9fafc" },
                                            transition: "0.2s ease-in-out",
                                        }}
                                    >
                                        <TableCell sx={{ fontSize: "12px", padding: "3px" }}>{index + 1}</TableCell>
                                        <TableCell sx={{ fontSize: "12px", padding: "3px" }}>{item.EmployeeCode}</TableCell>
                                        <TableCell sx={{ fontSize: "12px", padding: "3px" }}>{item.EmployeeName}</TableCell>
                                        <TableCell sx={{ fontSize: "12px", padding: "3px" }}>{item.DepartmentName}</TableCell>
                                        <TableCell sx={{ fontSize: "12px", padding: "3px" }}>{item.DesignationName}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: "12px", padding: "3px" }}>
                                            {hasEditMasterPermission && (
                                                <Tooltip title="Edit Employee">
                                                    <Link to={`/EmployeeEdit/${item.Id}`}>
                                                        <IconButton color="primary">
                                                            <EditIcon style={{ fontSize: '17px' }} />
                                                        </IconButton>
                                                    </Link>
                                                </Tooltip>
                                            )}

                                            {hasDeleteMasterPermission && (
                                                <Tooltip title="Delete Employee">
                                                    <IconButton onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))} color="error">
                                                        {item.IsActive ? <DeleteIcon style={{ fontSize: '17px' }} /> : <ActivateIcon style={{ fontSize: '17px' }} />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <Dialog
                                open={confirmDialogOpen}
                                onClose={closeConfirmDialog}
                                aria-labelledby="alert-dialog-title"
                                aria-describedby="alert-dialog-description"
                            >
                                <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                                <DialogContent>
                                    <DialogContentText id="alert-dialog-description">
                                        Are you sure you want to delete this record?
                                    </DialogContentText>
                                </DialogContent>

                                <DialogActions>
                                    <button onClick={closeConfirmDialog} className="bg-red-600 text-white border border-red-600 rounded px-4 py-2 mr-2">
                                        Cancel
                                    </button>
                                    <button onClick={handleDelete} className="bg-green-600 text-white border border-green-600 rounded px-4 py-2">
                                        Confirm
                                    </button>
                                </DialogActions>
                            </Dialog>
                        </Table>
                    </TableContainer>
                </div>

                {/* Footer */}
                <div className="text-center text-gray-500 text-xs mt-3">
                    <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.5 }} />
                    <b style={{ fontFamily: "Georgia, serif" }}>EFFTABS SOLUTIONS LLP</b>
                </div>
            </div>


        </div>

    );
}

export default EmployeeIndex;

